// js/custom-element/custom-html-element.js
var CustomHTMLElement = class extends HTMLElement {
  /**
   * Returns a promise that resolves when the element becomes visible in the viewport.
   * @param {Object} options - IntersectionObserver options
   * @param {string} [options.rootMargin='0px'] - Margin around the root
   * @param {number} [options.threshold=0] - Intersection threshold
   * @returns {Promise<void>}
   */
  untilVisible(options = {}) {
    return new Promise((resolve) => {
      const observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting) {
              observer.disconnect();
              resolve();
              return;
            }
          }
        },
        {
          rootMargin: options.rootMargin || "0px",
          threshold: options.threshold || 0,
        },
      );
      observer.observe(this);
    });
  }
};

// js/custom-element/ui/native-video.js
var NativeVideo = class extends CustomHTMLElement {
  constructor() {
    super();
    this.hasLoaded = false;
    if (this.autoPlay) {
      (async () => {
        await this.untilVisible({ rootMargin: "300px", threshold: 0 });
        this.play();
      })();
    } else {
      this.addEventListener("click", this.play.bind(this), { once: true });
    }
  }
  get autoPlay() {
    return this.hasAttribute("autoplay");
  }
  play() {
    if (!this.hasLoaded) {
      this._replaceContent();
    }
    this.querySelector("video").play();
  }
  pause() {
    if (this.hasLoaded) {
      this.querySelector("video").pause();
    }
  }
  _replaceContent() {
    let node = this.querySelector("template");
    if (!node) return;
    node = node.content.firstElementChild.cloneNode(true);
    if (!this.hasAttribute("autoplay")) {
      this.innerHTML = "";
    }
    this.appendChild(node);
    this.firstElementChild.addEventListener("play", () => {
      this.dispatchEvent(new CustomEvent("video:played", { bubbles: true }));
    });
    this.firstElementChild.addEventListener("pause", () => {
      this.dispatchEvent(new CustomEvent("video:paused", { bubbles: true }));
    });
    this.hasLoaded = true;
  }
};
window.customElements.define("native-video", NativeVideo);

// js/custom-element/ui/external-video.js
var ExternalVideo = class extends CustomHTMLElement {
  constructor() {
    super();
    this.hasLoaded = false;
    (async () => {
      if (this.autoPlay) {
        await this.untilVisible({ rootMargin: "300px", threshold: 0 });
        this.play();
      } else {
        this.addEventListener("click", this.play.bind(this), { once: true });
      }
    })();
  }
  get autoPlay() {
    return this.hasAttribute("autoplay");
  }
  get provider() {
    return this.getAttribute("provider");
  }
  async play() {
    if (!this.hasLoaded) {
      await this._setupPlayer();
    }
    if (this.provider === "youtube") {
      setTimeout(() => {
        this.querySelector("iframe").contentWindow.postMessage(
          JSON.stringify({ event: "command", func: "playVideo", args: "" }),
          "*",
        );
      }, 150);
    } else if (this.provider === "vimeo") {
      this.querySelector("iframe").contentWindow.postMessage(
        JSON.stringify({ method: "play" }),
        "*",
      );
    }
  }
  pause() {
    if (!this.hasLoaded) {
      return;
    }
    if (this.provider === "youtube") {
      this.querySelector("iframe").contentWindow.postMessage(
        JSON.stringify({ event: "command", func: "pauseVideo", args: "" }),
        "*",
      );
    } else if (this.provider === "vimeo") {
      this.querySelector("iframe").contentWindow.postMessage(
        JSON.stringify({ method: "pause" }),
        "*",
      );
    }
  }
  _setupPlayer() {
    if (this._setupPromise) {
      return this._setupPromise;
    }
    return (this._setupPromise = new Promise((resolve) => {
      const template2 = this.querySelector("template"),
        node = template2.content.firstElementChild.cloneNode(true);
      node.onload = () => {
        this.hasLoaded = true;
        resolve();
      };
      if (this.autoPlay) {
        template2.replaceWith(node);
      } else {
        this.innerHTML = "";
        this.appendChild(node);
      }
    }));
  }
};
window.customElements.define("external-video", ExternalVideo);

// js/custom-element/section/video-section-responsive.js
var VideoSectionResponsive = class extends HTMLElement {
  connectedCallback() {
    this._mql = window.matchMedia("(max-width: 740px)");
    this._handleChange = this._handleChange.bind(this);
    this._mql.addEventListener("change", this._handleChange);
  }

  disconnectedCallback() {
    if (this._mql) {
      this._mql.removeEventListener("change", this._handleChange);
    }
  }

  _handleChange(e) {
    const hideSelector = e.matches
      ? ".video-section__desktop"
      : ".video-section__mobile";
    const showSelector = e.matches
      ? ".video-section__mobile"
      : ".video-section__desktop";

    // Pause videos in the hidden container
    this.querySelectorAll(
      `${hideSelector} native-video, ${hideSelector} external-video`,
    ).forEach((video) => {
      video.pause();
    });

    // Play autoplay videos in the shown container
    this.querySelectorAll(
      `${showSelector} native-video[autoplay], ${showSelector} external-video[autoplay]`,
    ).forEach((video) => {
      video.play();
    });
  }
};
window.customElements.define(
  "video-section-responsive",
  VideoSectionResponsive,
);
